import { useRef, useLayoutEffect, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Instagram, Mail } from 'lucide-react';
import MagneticButton from '../MagneticButton';
import { toast } from 'sonner';
import { openWhatsAppEnquiry } from '../../utils/whatsappEnquiry';

gsap.registerPlugin(ScrollTrigger);

const WhatsAppIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
);

const SOCIALS = [
  { label: 'Instagram', href: 'https://www.instagram.com/risewithpresence', icon: <Instagram size={22} />, testId: 'contact-instagram-link', handle: '@risewithpresence' },
  { label: 'WhatsApp', href: 'https://wa.me/61488248572', icon: <WhatsAppIcon />, testId: 'contact-whatsapp-link', handle: '+61 488 248 572' },
  { label: 'Email', href: 'mailto:RiseWithPresence@gmail.com', icon: <Mail size={22} />, testId: 'contact-gmail-link', handle: 'RiseWithPresence@gmail.com' },
];

const Contact = () => {
  const root = useRef(null);
  const [showForm, setShowForm] = useState(false);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const lines = gsap.utils.toArray('.contact-line');
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: root.current,
          start: 'top top',
          end: '+=250%',
          pin: true,
          scrub: 1.4,
        },
      });
      lines.forEach((line, i) => {
        tl.fromTo(line, { opacity: 0, y: 50, filter: 'blur(8px)' }, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 1.8, ease: 'power2.out' }, i * 1.6);
      });
      tl.to(lines, { opacity: 0, y: -40, filter: 'blur(6px)', duration: 2, ease: 'power2.in' }, lines.length * 1.6 + 1.2);
      tl.fromTo('.contact-final', { opacity: 0, scale: 0.96 }, { opacity: 1, scale: 1, duration: 2.4, ease: 'power2.out' }, lines.length * 1.6 + 2.6);
    }, root);
    return () => ctx.revert();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    openWhatsAppEnquiry({
      name: formData.get('name'),
      email: formData.get('email'),
      message: formData.get('message'),
    });
    toast('Thank you for reaching out.', {
      description: 'Manvinder will be in touch with you very soon. Until then, breathe.',
    });
    e.target.reset();
    setShowForm(false);
  };

  return (
    <section id="contact" ref={root} data-testid="contact-section" className="relative h-[100svh] overflow-hidden bg-[#1A4D38]">
      <div
        className="absolute inset-0 opacity-25"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1426604966848-d7adac402bff?auto=format&fit=crop&w=1800&q=70)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <div className="breathing-light absolute top-1/4 left-1/2 -translate-x-1/2 w-[60vw] h-[60vw] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(201,139,58,0.22) 0%, transparent 65%)' }} />
      {[...Array(14)].map((_, i) => (
        <div
          key={i}
          className="particle"
          style={{
            width: `${2 + (i % 3)}px`,
            height: `${2 + (i % 3)}px`,
            left: `${(i * 7.3) % 100}%`,
            bottom: '-10px',
            animationDuration: `${18 + (i % 6) * 4}s`,
            animationDelay: `${i * 1.7}s`,
          }}
        />
      ))}

      <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
        <div className="space-y-6 md:space-y-8 absolute">
          <p className="contact-line font-serif-display font-light text-3xl sm:text-5xl lg:text-6xl text-[#F5F0E8] leading-[1.2]" style={{ opacity: 0 }}>
            You came here searching.
          </p>
          <p className="contact-line font-serif-display font-light italic text-3xl sm:text-5xl lg:text-6xl text-[#F5F0E8]/90 leading-[1.2]" style={{ opacity: 0 }}>
            Perhaps you were searching
          </p>
          <p className="contact-line font-serif-display font-light italic text-4xl sm:text-6xl lg:text-7xl text-[#C98B3A] leading-[1.2]" style={{ opacity: 0 }}>
            for yourself.
          </p>
        </div>

        <div className="contact-final absolute flex flex-col items-center" style={{ opacity: 0 }}>
          {!showForm ? (
            <>
              <MagneticButton
                testId="contact-begin-button"
                onClick={() => setShowForm(true)}
                className="px-12 py-5 rounded-full border border-[#F5F0E8]/50 text-[#FAFAF8] text-xs uppercase tracking-[0.35em] bg-[#F5F0E8]/5 hover:bg-[#F5F0E8]/15 transition-colors duration-700"
              >
                Begin Your Journey
              </MagneticButton>
              <div className="flex items-center gap-10 mt-16">
                {SOCIALS.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={s.testId}
                    aria-label={s.label}
                    className="group flex flex-col items-center gap-3 text-[#F5F0E8]/70 hover:text-[#C98B3A] transition-colors duration-500"
                  >
                    {s.icon}
                    <span className="text-[9px] uppercase tracking-[0.25em] opacity-0 group-hover:opacity-100 transition-opacity duration-500">{s.label}</span>
                  </a>
                ))}
              </div>
            </>
          ) : (
            <form onSubmit={handleSubmit} data-testid="contact-form" className="w-[86vw] max-w-md text-left">
              <p className="font-serif-display italic text-2xl text-[#F5F0E8] mb-10 text-center">Tell me where you are.</p>
              <div className="space-y-7">
                <input name="name" data-testid="contact-name-input" required placeholder="Your name" className="form-field !text-[#F5F0E8] !border-[#F5F0E8]/30 placeholder:!text-[#F5F0E8]/40" style={{ color: '#F5F0E8', borderBottomColor: 'rgba(245,240,232,0.3)' }} />
                <input name="email" data-testid="contact-email-input" required type="email" placeholder="Your email" className="form-field" style={{ color: '#F5F0E8', borderBottomColor: 'rgba(245,240,232,0.3)' }} />
                <textarea name="message" data-testid="contact-message-input" rows="3" placeholder="What brought you here today?" className="form-field resize-none" style={{ color: '#F5F0E8', borderBottomColor: 'rgba(245,240,232,0.3)' }} />
              </div>
              <div className="flex items-center gap-6 mt-10 justify-center">
                <MagneticButton
                  testId="contact-form-submit"
                  className="px-10 py-4 rounded-full bg-[#C98B3A] text-[#1A4D38] text-xs uppercase tracking-[0.3em] font-medium"
                >
                  Send
                </MagneticButton>
                <button type="button" data-testid="contact-form-cancel" onClick={() => setShowForm(false)} className="text-[11px] uppercase tracking-[0.25em] text-[#F5F0E8]/50 hover:text-[#F5F0E8] transition-colors duration-500">
                  Not yet
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default Contact;
